// ── 대화목록 사이드바 ─────────────────────────────
// 데스크톱=좌측 컬럼 / 모바일=off-canvas 드로어. 새 대화·전체대화·프로젝트·Artifacts 진입,
// 검색·즐겨찾기/보관함/태그 필터, 대화행(즐겨찾기·제목수정·태그·보관·삭제).
// 순수 프레젠테이션: 모든 상태·핸들러는 ChatPage에서 props로 주입(동작 100% 동일).
import { Fragment } from 'react';
import { startColResize } from '../../utils/colResize';
import { IconArchive, IconFolder, IconMessageCircle, IconPalette, IconPencil, IconPlus, IconRestore, IconSearch, IconSettings, IconStar, IconTag, IconTrash } from '@tabler/icons-react';
import { COLORS } from '../../utils/uiStyles';
import { kstDateOf, kstToday, kstDaysAgo, fmtKstShort } from '../../utils/kstTime';

const filterChipStyle = (active, activeBg = COLORS.primarySoft, activeColor = COLORS.selectedText) => ({
  fontSize: '12px',
  padding: '3px 10px',
  borderRadius: '20px',
  cursor: 'pointer',
  border: `1px solid ${active ? 'var(--gw-border, #e5e5e2)' : 'var(--gw-line, #eeeeec)'}`,
  background: active ? activeBg : 'transparent',
  color: active ? activeColor : 'var(--gw-secondary, #64635f)',
});

// 일반 목록의 날짜 칸 순서(맨 뒤 '' = 날짜 없는 행 · 머리 없음). 칸 안은 목록 순서 그대로.
const GROUP_ORDER = ['즐겨찾기', '오늘', '어제', '이전 7일', '이전', ''];

// 행 시각 줄(생성 시각 · KST). 오늘·어제는 시:분, 올해는 «M월 D일», 그 밖은 «YYYY. M. D.». 날짜를 못 읽으면 null.
function rowTimeLabel(t, today, y1) {
  const k = kstDateOf(t);
  if (!k) return null;
  if (k === today) return `오늘 · ${fmtKstShort(t).slice(-5)}`;
  if (k === y1) return `어제 · ${fmtKstShort(t).slice(-5)}`;
  if (k.slice(0, 4) === today.slice(0, 4)) return `${Number(k.slice(5, 7))}월 ${Number(k.slice(8, 10))}일`;
  return `${k.slice(0, 4)}. ${Number(k.slice(5, 7))}. ${Number(k.slice(8, 10))}.`;
}

export default function ConversationList({
  mobile, convDrawerOpen, setConvDrawerOpen,
  listWidth, setListWidth,
  startNew, selectProject, selectedProjectId,
  projectsOpen, setProjectsOpen, projects, openProjSettings,
  setShowNewProj, setNewProjName, setShowArtifacts,
  searchQuery, handleSearch, searchResults,
  favOnly, setFavOnly, archiveView, setArchiveView, tagFilter, setTagFilter,
  conversations, currentConvId, genConvIds,
  toggleFavorite, editTags, toggleArchive, handleDeleteConv, loadConversation,
  editingTitleId, setEditingTitleId, editingTitleVal, setEditingTitleVal, saveTitle,
}) {
  const trashView = archiveView === 'trash';
  const activeCount = trashView ? 0 : conversations.filter((c) => !c.deleted_at && !c.is_archived).length;
  const sidebarWidth = listWidth === 280 ? 304 : listWidth;

  return (
    <>
      <style>{`.conv-row-actions{opacity:0;pointer-events:none;transition:opacity .14s ease}.conv-row:hover .conv-row-actions,.conv-row:focus-within .conv-row-actions{opacity:1;pointer-events:auto}@media (hover:none){.conv-row-actions{opacity:1;pointer-events:auto}}@media (hover:hover){.conv-row-main{position:relative}.conv-row{--conv-row-bg:#fbfbfa}.conv-row:hover{--conv-row-bg:#efefed}.conv-row-actions{position:absolute;right:0;top:50%;transform:translateY(-50%);padding-left:14px;border-radius:6px;background:linear-gradient(to right,transparent,var(--conv-row-bg) 10px)}}`}</style>
      <style>{'@media (hover:hover){.conv-new-btn:hover{background:var(--gw-hover, #efefed)!important}}'}</style>
      {mobile && convDrawerOpen && (
        <div onClick={() => setConvDrawerOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 1101, background: 'rgba(0,0,0,.4)' }} />
      )}
      <aside className="sb-scroll" style={mobile
        ? { position: 'fixed', top: 0, left: 0, bottom: 0, width: '304px', maxWidth: '88vw', zIndex: 1102, background: '#fbfbfa', borderRight: '1px solid #eeeeec', display: 'flex', flexDirection: 'column', overflow: 'hidden', transform: convDrawerOpen ? 'translateX(0)' : 'translateX(-100%)', transition: 'transform .25s ease', boxShadow: convDrawerOpen ? '2px 0 16px rgba(0,0,0,.18)' : 'none' }
        : { width: sidebarWidth, minWidth: sidebarWidth, background: '#fbfbfa', borderRight: '1px solid #eeeeec', display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
        {!mobile && (
          <div onMouseDown={(e) => startColResize(e, setListWidth, 'chatListWidth', 180, 460)} title="드래그하여 폭 조절"
            style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: '5px', cursor: 'col-resize', zIndex: 10 }}
            onMouseEnter={(e) => { e.currentTarget.style.background = COLORS.primaryBorder; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }} />
        )}
      <div style={{ padding: '22px 13px 8px', borderBottom: '1px solid var(--gw-line, #eeeeec)' }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--gw-text, #32312e)', margin: '0 7px 16px' }}>내 대화</div>
        <button type="button" className="conv-new-btn" onClick={startNew}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100%', height: '31px', padding: '0 11px', marginBottom: '15px', background: 'var(--gw-surface, #fff)', border: '1px solid var(--gw-border, #e5e5e2)', borderRadius: '5px', boxShadow: '0 1px 1px var(--gw-line, #eeeeec)', color: 'var(--gw-text, #32312e)', fontSize: '13px', fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer', boxSizing: 'border-box' }}>
          <IconPlus size={15} stroke={1.9} aria-hidden="true" /><span>새 대화</span>
        </button>
        <div onClick={() => selectProject(null)}
          style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', borderRadius: '8px', cursor: 'pointer', color: !selectedProjectId ? COLORS.selectedText : 'var(--gw-text, #32312e)', fontSize: '14px', background: !selectedProjectId ? COLORS.primarySoft : 'transparent' }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--gw-hover, #efefed)'}
          onMouseLeave={(e) => e.currentTarget.style.background = !selectedProjectId ? COLORS.primarySoft : 'transparent'}>
          <span style={{ width: '18px', display: 'inline-flex', justifyContent: 'center', flexShrink: 0 }}><IconMessageCircle size={16} stroke={1.8} aria-hidden="true" /></span><span style={{ minWidth: 0 }}>전체 대화</span>
        </div>
        <div onClick={() => setProjectsOpen((v) => !v)}
          style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', borderRadius: '8px', cursor: 'pointer', color: 'var(--gw-text, #32312e)', fontSize: '14px' }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--gw-hover, #efefed)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
          <span style={{ width: '18px', display: 'inline-flex', justifyContent: 'center', flexShrink: 0 }}><IconFolder size={16} stroke={1.8} aria-hidden="true" /></span><span style={{ minWidth: 0 }}>프로젝트</span>
          <span style={{ marginLeft: 'auto', fontSize: '10px', color: 'var(--gw-muted, #92918d)' }}>{projectsOpen ? '▾' : '▸'}</span>
        </div>
        {projectsOpen && (
          <div style={{ paddingLeft: '20px', paddingRight: '4px', marginBottom: '2px' }}>
            <div className="sb-scroll" style={{ display: 'flex', flexDirection: 'column', gap: '2px', maxHeight: '140px', overflowY: 'auto' }}>
              {projects.length === 0 && (
                <div style={{ fontSize: '12px', color: 'var(--gw-muted, #92918d)', padding: '6px 8px' }}>프로젝트가 없습니다.</div>
              )}
              {projects.map((p) => {
                const selected = String(selectedProjectId || '') === String(p.id);
                const role = p.access_role || p.role || (p.shared ? 'viewer' : 'owner');
                const isOwner = role === 'owner' || p.can_manage_shares;
                const canEdit = isOwner || role === 'editor' || p.can_edit;
                const isShared = p.shared || role !== 'owner';
                const settingsRestriction = `${role} 역할은 프로젝트 설정을 사용할 수 없습니다. editor 이상 권한이 필요합니다.`;
                return (
                  <div key={p.id} onClick={() => selectProject(p.id)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px', padding: '5px 8px', borderRadius: '6px', cursor: 'pointer', background: selected ? COLORS.primarySoft : 'transparent' }}>
                    <span style={{ fontSize: '12px', color: selected ? COLORS.selectedText : 'var(--gw-text, #32312e)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{p.name}</span>
                      <span title={isShared ? `공유받음 · ${role}` : role} style={{ fontSize: '10px', color: isShared ? '#0369a1' : 'var(--gw-secondary, #64635f)', background: isShared ? '#e0f2fe' : 'var(--gw-hover, #efefed)', borderRadius: '6px', padding: '1px 5px', flexShrink: 0 }}>
                        {role}
                      </span>
                    </span>
                    {selected && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (canEdit) openProjSettings(p);
                          }}
                          disabled={!canEdit}
                          title={canEdit ? '프로젝트 설정' : settingsRestriction}
                          aria-label={canEdit ? '프로젝트 설정' : settingsRestriction}
                          style={{ background: 'none', border: 'none', color: canEdit ? 'var(--gw-secondary, #64635f)' : '#cbd5e1', cursor: canEdit ? 'pointer' : 'not-allowed', fontSize: '13px', flexShrink: 0, padding: '0 2px', display: 'inline-flex', alignItems: 'center' }}
                        ><IconSettings size={14} stroke={1.8} aria-hidden="true" /></button>
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            <button onClick={() => { setShowNewProj(true); setNewProjName(''); }}
              style={{ background: 'none', border: 'none', color: 'var(--gw-muted, #92918d)', cursor: 'pointer', fontSize: '12px', padding: '4px 8px', textAlign: 'left' }}>+ 새 프로젝트</button>
          </div>
        )}
        <div onClick={() => setShowArtifacts(true)}
          style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', borderRadius: '8px', cursor: 'pointer', color: 'var(--gw-text, #32312e)', fontSize: '14px' }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--gw-hover, #efefed)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
          <span style={{ width: '18px', display: 'inline-flex', justifyContent: 'center', flexShrink: 0 }}><IconPalette size={16} stroke={1.8} aria-hidden="true" /></span><span style={{ minWidth: 0 }}>Artifacts</span>
        </div>
      </div>
      {/* 검색창 */}
      <div style={{ padding: '8px 13px', borderBottom: '1px solid var(--gw-line, #eeeeec)' }}>
        <div style={{ position: 'relative' }}>
          <IconSearch size={14} stroke={1.8} aria-hidden="true" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--gw-muted, #92918d)', pointerEvents: 'none' }} />
          <input
            value={searchQuery}
            onChange={handleSearch}
            disabled={trashView}
            placeholder={trashView ? '휴지통에서는 검색할 수 없습니다.' : '대화 검색...'}
            style={{ width: '100%', height: '30px', padding: '0 9px 0 32px', background: 'transparent', border: '1px solid var(--gw-line, #eeeeec)', borderRadius: '5px', color: 'var(--gw-text, #32312e)', fontSize: '12px', outline: 'none', boxSizing: 'border-box' }}
          />
        </div>
      </div>

      {/* 즐겨찾기·태그 필터 바 */}
      {searchResults === null && (
        <div style={{ padding: '8px 13px 0', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          <button onClick={() => { setFavOnly(false); setArchiveView(false); }}
            style={filterChipStyle(!favOnly && !archiveView, COLORS.primarySoft, COLORS.selectedText)}>전체</button>
          <button onClick={() => { setFavOnly(true); setArchiveView(false); }}
            style={{ ...filterChipStyle(favOnly && !archiveView, '#fffbeb', '#92400e'), display: 'inline-flex', alignItems: 'center', gap: '4px' }}><IconStar size={12} stroke={1.8} fill={favOnly && !archiveView ? 'currentColor' : 'none'} aria-hidden="true" />즐겨찾기</button>
          <button onClick={() => { setArchiveView(true); setFavOnly(false); }}
            style={{ ...filterChipStyle(archiveView === true, '#f0fdf4', '#047857'), display: 'inline-flex', alignItems: 'center', gap: '4px' }}><IconArchive size={12} stroke={1.8} aria-hidden="true" />보관함</button>
          <button onClick={() => { setArchiveView('trash'); setFavOnly(false); }}
            style={{ ...filterChipStyle(trashView, '#fff7ed', '#c2410c'), display: 'inline-flex', alignItems: 'center', gap: '4px' }}><IconTrash size={12} stroke={1.8} aria-hidden="true" />휴지통</button>
          {tagFilter && (
            <span onClick={() => setTagFilter(null)} title="태그 필터 해제"
              style={{ ...filterChipStyle(true, COLORS.primarySoft, COLORS.selectedText), padding: '3px 9px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}><IconTag size={12} stroke={1.8} aria-hidden="true" />{tagFilter} ✕</span>
          )}
          {!trashView && <span title="보관 중인 대화 수 / 최대 보관 수(보관함 제외)" style={{ marginLeft: 'auto', fontSize: '11px', fontWeight: '600', color: activeCount >= 50 ? '#fca5a5' : 'var(--gw-muted, #92918d)' }}>
            {activeCount}/50
          </span>}
        </div>
      )}

      <div className="sb-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '4px 13px 16px' }}>
        {(() => {
          const today = kstToday();
          const y1 = kstDaysAgo(1);
          const d7 = kstDaysAgo(7);
          let list;
          if (searchResults !== null) {
            list = searchResults;
          } else if (trashView) {
            list = conversations.filter((c) => !!c.deleted_at);
          } else {
            list = conversations.filter((c) => !c.deleted_at);
            list = list.filter((c) => selectedProjectId ? String(c.project_id || '') === String(selectedProjectId) : !c.project_id);
            if (archiveView) list = list.filter((c) => c.is_archived);   // 보관함: 보관된 것만
            else list = list.filter((c) => !c.is_archived);              // 일반 목록: 보관함 숨김
            if (favOnly) list = list.filter((c) => c.is_favorite);
            if (tagFilter) list = list.filter((c) => (c.tags || '').split(',').map((t) => t.trim()).filter(Boolean).includes(tagFilter));
            list = [...list].sort((a, b) => (b.is_favorite ? 1 : 0) - (a.is_favorite ? 1 : 0));
          }
          if (!list.length) {
            const emptyText = searchResults !== null
              ? '검색 결과가 없습니다.'
              : trashView
                ? '휴지통이 비어 있습니다.'
                : archiveView
                ? '보관된 대화가 없습니다.'
                : favOnly
                  ? '즐겨찾기 대화가 없습니다.'
                  : tagFilter
                    ? '해당 태그의 대화가 없습니다.'
                    : '대화가 없습니다.';
            return (
              <div style={{ color: 'var(--gw-muted, #92918d)', fontSize: '12px', textAlign: 'center', padding: '28px 10px', lineHeight: 1.6 }}>
                {emptyText}
              </div>
            );
          }
          const renderRow = (c) => {
            const tagArr = (c.tags || '').split(',').map((t) => t.trim()).filter(Boolean);
            const rowTime = (searchResults !== null || trashView) ? null : rowTimeLabel(c.created_at || c.updated_at, today, y1);
            // 버튼 묶음을 줄 안에 둘 행: 모바일(항상 보임)·제목 편집 중(입력칸 끝)·응답 작성 중(회전 표시). 그 밖은 CSS 가 줄 끝에 겹쳐 띄운다.
            const actionsInFlow = mobile || editingTitleId === c.id || genConvIds.has(c.id);
            return (
              <div key={c.id} className="conv-row"
                style={{ padding: '10px 9px', borderRadius: '5px', marginBottom: '3px', cursor: 'pointer',
                  background: currentConvId === c.id ? '#eaeae7' : 'transparent',
                  border: '1px solid transparent',
                  ...(currentConvId === c.id ? { '--conv-row-bg': '#eaeae7' } : {}) }}
                onMouseEnter={(e) => { if (currentConvId !== c.id) e.currentTarget.style.background = '#efefed'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = currentConvId === c.id ? '#eaeae7' : 'transparent'; }}>
                <div className="conv-row-main" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {searchResults === null && !trashView && (
                    <button onClick={(e) => toggleFavorite(c, e)} title="즐겨찾기"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', fontSize: '13px', lineHeight: 1, flexShrink: 0, color: c.is_favorite ? '#fbbf24' : 'var(--gw-secondary, #64635f)', display: 'inline-flex', alignItems: 'center' }}><IconStar size={14} stroke={1.8} fill={c.is_favorite ? 'currentColor' : 'none'} aria-hidden="true" /></button>
                  )}
                  {editingTitleId === c.id ? (
                    <input
                      autoFocus
                      value={editingTitleVal}
                      onChange={(e) => setEditingTitleVal(e.target.value)}
                      onBlur={() => saveTitle(c.id)}
                      onKeyDown={(e) => { if (e.key === 'Enter') saveTitle(c.id); if (e.key === 'Escape') setEditingTitleId(null); }}
                      onClick={(e) => e.stopPropagation()}
                      style={{ flex: 1, minWidth: 0, background: '#fbfbfa', border: '1px solid #e5e5e2', borderRadius: '4px', color: '#32312e', fontSize: '13px', padding: '2px 6px', outline: 'none' }}
                    />
                  ) : (
                    <span
                      onClick={() => { if (!trashView) loadConversation(c.id); }}
                      onDoubleClick={(e) => { if (!trashView) { e.stopPropagation(); setEditingTitleId(c.id); setEditingTitleVal(c.title); } }}
                      style={{ fontSize: '13px', color: '#32312e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}
                      title={trashView ? '휴지통의 대화' : '더블클릭으로 제목 수정'}
                    >{c.title}</span>
                  )}
                  {genConvIds.has(c.id) && (
                    <span title="응답 작성 중" aria-label="응답 작성 중"
                      style={{ width: '10px', height: '10px', border: '2px solid rgba(35,131,226,.28)', borderTopColor: COLORS.primary, borderRadius: '50%', display: 'inline-block', flexShrink: 0, animation: 'docspin 0.7s linear infinite' }} />
                  )}
                  <div className="conv-row-actions" style={{ display: 'flex', alignItems: 'center', gap: '1px', flexShrink: 0, opacity: mobile ? 1 : undefined, pointerEvents: mobile ? 'auto' : undefined,
                    ...(actionsInFlow ? { position: 'static', transform: 'none', paddingLeft: 0, background: 'transparent' } : {}) }}>
                    {searchResults === null && !trashView && editingTitleId !== c.id && (
                      <button onClick={(e) => { e.stopPropagation(); setEditingTitleId(c.id); setEditingTitleVal(c.title); }} title="제목 수정"
                        style={{ background: 'none', border: 'none', color: 'var(--gw-secondary, #64635f)', cursor: 'pointer', padding: '0 1px', fontSize: '11px', lineHeight: 1, flexShrink: 0, display: 'inline-flex', alignItems: 'center' }}><IconPencil size={13} stroke={1.8} aria-hidden="true" /></button>
                    )}
                    {searchResults === null && !trashView && (
                      <button onClick={(e) => editTags(c, e)} title="태그 편집"
                        style={{ background: 'none', border: 'none', color: 'var(--gw-secondary, #64635f)', cursor: 'pointer', padding: '0 1px', fontSize: '11px', lineHeight: 1, flexShrink: 0, display: 'inline-flex', alignItems: 'center' }}><IconTag size={13} stroke={1.8} aria-hidden="true" /></button>
                    )}
                    {searchResults === null && !trashView && (
                      <button onClick={(e) => toggleArchive(c, e)} title={c.is_archived ? '보관 해제' : '보관함에 보관(영구 보관)'}
                        style={{ background: 'none', border: 'none', color: c.is_archived ? '#059669' : 'var(--gw-secondary, #64635f)', cursor: 'pointer', padding: '0 1px', fontSize: '11px', lineHeight: 1, flexShrink: 0, display: 'inline-flex', alignItems: 'center' }}><IconArchive size={13} stroke={1.8} aria-hidden="true" /></button>
                    )}
                    {trashView ? (
                      <>
                        <button onClick={(e) => handleDeleteConv(c.id, e, 'restore')} title="복원"
                          style={{ background: 'none', border: 'none', color: '#047857', cursor: 'pointer', padding: '0 2px', display: 'inline-flex' }}><IconRestore size={14} stroke={1.8} aria-hidden="true" /></button>
                        <button onClick={(e) => handleDeleteConv(c.id, e, 'permanent')} title="영구 삭제"
                          style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: '0 2px', display: 'inline-flex' }}><IconTrash size={14} stroke={1.8} aria-hidden="true" /></button>
                      </>
                    ) : (
                      <button onClick={(e) => handleDeleteConv(c.id, e)} disabled={genConvIds.has(c.id)}
                        title={genConvIds.has(c.id) ? '답변 생성 중에는 삭제할 수 없습니다.' : '휴지통으로 이동'}
                        style={{ background: 'none', border: 'none', color: 'var(--gw-muted, #92918d)', cursor: genConvIds.has(c.id) ? 'not-allowed' : 'pointer', padding: '0 2px', fontSize: '16px', lineHeight: 1, flexShrink: 0 }}>×</button>
                    )}
                  </div>
                </div>
                {rowTime && <div className="conv-row-time" style={{ fontSize: '10px', color: 'var(--gw-muted, #92918d)', marginTop: '5px', marginLeft: '22px' }}>{rowTime}</div>}
                {!trashView && tagArr.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginTop: '3px', marginLeft: '20px' }}>
                    {tagArr.map((t) => (
                      <span key={t} onClick={(e) => { e.stopPropagation(); setTagFilter(t); }}
                        style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '10px', background: COLORS.primarySoft, color: COLORS.selectedText, cursor: 'pointer', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t}</span>
                    ))}
                  </div>
                )}
              </div>
            );
          };
          // 일반 목록만 날짜 칸으로 나눈다(검색 결과·휴지통은 칸 없이). 즐겨찾기 보기에선 즐겨찾기 칸을 따로 두지 않는다.
          const groupOf = (c) => {
            if (!favOnly && c.is_favorite) return '즐겨찾기';
            const k = kstDateOf(c.created_at || c.updated_at);
            if (!k) return '';
            if (k === today) return '오늘';
            if (k === y1) return '어제';
            if (k >= d7) return '이전 7일';
            return '이전';
          };
          const groups = (searchResults === null && !trashView)
            ? GROUP_ORDER.map((name) => ({ name, rows: list.filter((c) => groupOf(c) === name) })).filter((g) => g.rows.length)
            : [];
          // 날짜 있는 행도 즐겨찾기 칸도 없으면 머리 없이 지금처럼 그린다.
          if (!groups.some((g) => g.name)) return list.map(renderRow);
          return groups.map(({ name, rows }) => (
            <Fragment key={name || '_'}>
              {name && <div className="conv-group-label" style={{ fontSize: '11px', color: 'var(--gw-muted, #92918d)', padding: '20px 8px 8px' }}>{name}</div>}
              {rows.map(renderRow)}
            </Fragment>
          ));
        })()}
      </div>
      </aside>
    </>
  );
}
